import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip, Paper, TextField, InputAdornment } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import ShortTextIcon from '@mui/icons-material/ShortText';
import ExpandIcon from '@mui/icons-material/Expand';
import SendIcon from '@mui/icons-material/Send';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import './TextSelectionMenu.css';

/**
 * TextSelectionMenu - Floating menu that appears when text is selected
 * Provides quick actions to send selected text to the reasoning agent
 */
const TextSelectionMenu = ({ onSendToAgent, containerRef }) => {
  const [selectedText, setSelectedText] = useState('');
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0, visible: false });
  const [customInstruction, setCustomInstruction] = useState('');
  const [showInput, setShowInput] = useState(false);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleSelection = () => {
      // Don't handle selection changes when input is focused or visible
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.closest('.custom-instruction-input') ||
        activeElement.closest('.MuiTextField-root') ||
        activeElement.closest('.MuiInputBase-root')
      );
      
      // Skip selection handling if input is visible or focused
      if (isInputFocused || showInput) {
        return;
      }
      
      const selection = window.getSelection();
      
      // Check if selection has content
      if (!selection || selection.rangeCount === 0) {
        setMenuPosition({ x: 0, y: 0, visible: false });
        return;
      }
      
      const text = selection.toString().trim();
      const range = selection.getRangeAt(0);
      
      // Check if selection is within our container (for Lexical editor compatibility)
      let isInContainer = true;
      if (containerRef?.current) {
        const containerElement = containerRef.current;
        const anchorNode = selection.anchorNode;
        const focusNode = selection.focusNode;
        
        // Check if selection nodes are within container or its descendants
        const isAnchorInContainer = containerElement.contains(anchorNode) || 
                                   containerElement === anchorNode ||
                                   containerElement.contains(anchorNode?.parentElement);
        const isFocusInContainer = containerElement.contains(focusNode) || 
                                  containerElement === focusNode ||
                                  containerElement.contains(focusNode?.parentElement);
        
        isInContainer = isAnchorInContainer && isFocusInContainer;
        
        // Also check bounding rect as fallback
        if (isInContainer && range) {
          const rect = range.getBoundingClientRect();
          const containerRect = containerElement.getBoundingClientRect();
          const rectInBounds = 
            rect.top >= containerRect.top &&
            rect.bottom <= containerRect.bottom &&
            rect.left >= containerRect.left &&
            rect.right <= containerRect.right;
          
          // Use bounding rect check if DOM contains check passed
          if (!rectInBounds && rect.width > 0 && rect.height > 0) {
            isInContainer = false;
          }
        }
      }

      if (text && text.length > 0 && isInContainer) {
        // Get the bounding rect of the selection
        const rect = range.getBoundingClientRect();

        // Position menu above the selection
        const x = rect.left + rect.width / 2;
        const y = rect.top - 10; // 10px above selection

        setSelectedText(text);
        setMenuPosition({ x, y, visible: true });
        setShowInput(false); // Reset input visibility on new selection
        setCustomInstruction(''); // Clear custom instruction on new selection
        
        console.log('📝 TextSelectionMenu - Selection detected:', {
          textLength: text.length,
          textPreview: text.substring(0, 50),
          isInContainer,
          containerRefExists: !!containerRef?.current
        });
      } else {
        setMenuPosition({ x: 0, y: 0, visible: false });
        setShowInput(false);
        setCustomInstruction('');
        if (text && text.length > 0 && !isInContainer) {
          console.log('📝 TextSelectionMenu - Selection outside container, hiding menu');
        }
      }
    };

    const handleClickOutside = (event) => {
      // Don't close if clicking inside the menu or input field
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        // Check if clicking on input or its container
        const isInputClick = event.target.closest('.custom-instruction-input') || 
                            event.target.closest('.MuiTextField-root') ||
                            event.target.closest('.MuiInputBase-input');
        
        if (!isInputClick) {
          // Clear selection if clicking outside
          const selection = window.getSelection();
          if (selection && !selection.toString()) {
            setMenuPosition({ x: 0, y: 0, visible: false });
            setShowInput(false);
            setCustomInstruction('');
          }
        }
      }
    };

    // Listen for selection changes
    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('keyup', handleSelection);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('keyup', handleSelection);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [containerRef, showInput]); // Include showInput in dependencies

  const handleAction = (action) => {
    // Capture the selected text immediately from the DOM selection
    // This ensures we get it even if the state hasn't updated or selection is cleared
    const currentSelection = window.getSelection();
    const currentSelectedText = currentSelection.toString().trim() || selectedText;
    
    if (!currentSelectedText) {
      console.warn('⚠️ No selected text available!', {
        fromState: selectedText,
        fromSelection: currentSelection.toString().trim(),
        selectionRangeCount: currentSelection.rangeCount
      });
      return;
    }

    // Use the captured text (prefer current selection over state)
    const textToUse = currentSelectedText;
    
    let instruction = '';
    switch (action) {
      case 'improve':
        instruction = `Improve this text while maintaining its meaning:\n\n${textToUse}`;
        break;
      case 'shorten':
        instruction = `Make this text more concise:\n\n${textToUse}`;
        break;
      case 'expand':
        instruction = `Expand and elaborate on this text:\n\n${textToUse}`;
        break;
      case 'rewrite':
        instruction = `Rewrite this text with better clarity:\n\n${textToUse}`;
        break;
      case 'custom':
        // Use custom instruction if provided, otherwise just send selected text
        instruction = customInstruction.trim() 
          ? `${customInstruction.trim()}\n\nSelected text:\n${textToUse}`
          : textToUse;
        break;
      default:
        instruction = textToUse;
    }

    console.log('📝 TextSelectionMenu - Creating instruction:', {
      action,
      selectedTextLength: textToUse.length,
      selectedTextPreview: textToUse.substring(0, 100),
      instructionLength: instruction.length,
      instructionPreview: instruction.substring(0, 200),
      includesSelectedText: instruction.includes(textToUse),
      source: currentSelection.toString().trim() ? 'currentSelection' : 'state',
      isCustom: action === 'custom'
    });

    onSendToAgent(instruction, textToUse);
    
    // Clear selection and hide menu AFTER sending
    window.getSelection().removeAllRanges();
    setMenuPosition({ x: 0, y: 0, visible: false });
    setSelectedText('');
    setCustomInstruction('');
    setShowInput(false);
  };

  const handleCustomSubmit = () => {
    if (customInstruction.trim() || selectedText) {
      handleAction('custom');
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomSubmit();
    }
  };

  // Focus input when it becomes visible
  useEffect(() => {
    if (showInput && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [showInput]);

  if (!menuPosition.visible) {
    return null;
  }

  return (
    <Paper
      ref={menuRef}
      className={`text-selection-menu ${showInput ? 'with-input' : ''}`}
      elevation={8}
      style={{
        position: 'fixed',
        left: `${menuPosition.x}px`,
        top: `${menuPosition.y}px`,
        transform: 'translate(-50%, -100%)',
        zIndex: 10000,
        background: '#FFFFFF',
        border: '1px solid #E0E0E0',
      }}
      onMouseDown={(e) => {
        // Prevent clicks inside menu from clearing selection
        // But allow input field to work normally
        const isInputField = e.target.closest('.custom-instruction-input') ||
                            e.target.closest('.MuiTextField-root') ||
                            e.target.closest('.MuiInputBase-input') ||
                            e.target.closest('.MuiInputBase-root') ||
                            e.target.tagName === 'INPUT';
        if (!isInputField) {
          e.stopPropagation();
        }
      }}
    >
      <div className="selection-menu-content">
        <div className="selection-menu-actions">
          <Tooltip title="Improve writing" placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAction('improve');
              }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent selection from being cleared
              }}
              className="selection-action-btn"
            >
              <AutoAwesomeIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Make shorter" placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAction('shorten');
              }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent selection from being cleared
              }}
              className="selection-action-btn"
            >
              <ShortTextIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Expand text" placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAction('expand');
              }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent selection from being cleared
              }}
              className="selection-action-btn"
            >
              <ExpandIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Rewrite" placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAction('rewrite');
              }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent selection from being cleared
              }}
              className="selection-action-btn"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={showInput ? "Hide custom input" : "Custom instruction"} placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowInput(!showInput);
              }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent selection from being cleared
              }}
              className={`selection-action-btn ${showInput ? 'active' : ''}`}
            >
              <TextFieldsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        {showInput && (
          <div 
            className="custom-instruction-input"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <TextField
              inputRef={inputRef}
              size="small"
              multiline
              minRows={1}
              maxRows={4}
              placeholder="Type your instruction..."
              value={customInstruction}
              onChange={(e) => {
                setCustomInstruction(e.target.value);
              }}
              onKeyPress={handleInputKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCustomSubmit();
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      disabled={!customInstruction.trim() && !selectedText}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className="custom-input-field"
              autoFocus
            />
          </div>
        )}
      </div>
      
      <div className="selected-text-preview">
        {selectedText.length > 60 
          ? selectedText.substring(0, 60) + '...' 
          : selectedText}
      </div>
    </Paper>
  );
};

TextSelectionMenu.propTypes = {
  /** Callback when user wants to send selected text to agent */
  onSendToAgent: PropTypes.func.isRequired,
  /** Reference to the container element to check if selection is within bounds */
  containerRef: PropTypes.object,
};

export default TextSelectionMenu;

